import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Measurement } from "../../../models/measurement";
import {
  DEFAULT_MAX_LENGTH,
  handleGetConvertValue,
  handleSaveConvertValue,
  MappingNameMetric,
} from "../../../util/converts";
import Input, { InputProps } from "../Input";

interface InputMeasurementProps extends InputProps {
  measurement?: Measurement;
  change?: (value: number) => void;
  error?: boolean;
  errorMessage?: string;
  decimalPlacesCustom?: number;
  showMeasurement?: boolean;
}

const InputMeasurement = ({
  value,
  change,
  measurement,
  decimalPlacesCustom,
  showMeasurement = true,
  ...rest
}: InputMeasurementProps) => {
  const { t: translate } = useTranslation();
  const getMaxLength = useMemo(() => {
    return decimalPlacesCustom || measurement?.decimalPlaces || DEFAULT_MAX_LENGTH;
  }, [measurement, decimalPlacesCustom]);

  const [stateTextValue, setStateTextValue] = useState<string>(
    String(handleGetConvertValue(Number(value), measurement?.code, getMaxLength))
  );

  const [stateValue, setStateValue] = useState<number>(Number(value));
  const [numberIsInvalid, setNumberIsInvalid] = useState<boolean>(false);
  const [onFocused, setOnFocused] = useState<boolean>(false);

  const updateCurrentValue = (value: number | string, measurement?: string): boolean => {
    if (!measurement) return true;

    const maxLength = getMaxLength;

    if (String(Number(value) % 1)?.length >= maxLength) return true;

    return false;
  };

  useEffect(() => {
    if (value !== stateValue) setStateValue(Number(value));
  }, [value]);
  useEffect(() => {
    if (String(stateValue) !== stateTextValue && !onFocused)
      setStateTextValue(
        String(handleGetConvertValue(Number(stateValue), measurement?.code, getMaxLength))
      );
  }, [stateValue]);

  const onChangeState = (inputValue: string | number) => {
    if (numberIsInvalid) return;
    if (onChange) onChange(inputValue);
    else setStateValue(Number(inputValue));
  };

  const onChange = (value: string | number) => {
    const nValue = Number(value);

    if (change) change(nValue);
  };

  return (
    <Input
      {...rest}
      value={stateTextValue}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        const numberRegex = /^([0-9]+)?(\.([0-9]+)?)?([,.][0-9]+)?$/;

        if (numberRegex.test(event.target.value)) {
          try {
            const convertedValue = handleSaveConvertValue(event.target.value, measurement?.code);

            setStateTextValue(event.target.value);

            if (Number.isNaN(convertedValue)) setNumberIsInvalid(true);
            else {
              setNumberIsInvalid(false);
              onChangeState(convertedValue);
            }
          } catch (err) {
            setNumberIsInvalid(true);
          }
        }
      }}
      error={numberIsInvalid || rest?.error || false}
      rightIcon={
        showMeasurement && measurement?.code
          ? `${MappingNameMetric[measurement.code as keyof typeof MappingNameMetric]}`
          : undefined
      }
      errorMessage={numberIsInvalid ? translate("invalidNumber") : rest.errorMessage}
      onBlur={() => {
        if (updateCurrentValue(stateTextValue, measurement?.code)) {
          setStateTextValue(Number(stateTextValue)?.toFixed(getMaxLength));
        }
        setOnFocused(false);
      }}
      onFocus={() => setOnFocused(true)}
    />
  );
};

export default InputMeasurement;
